/**
 * Chakra's default token scales, resized to 75%.
 *
 * Chakra ships these as rem values off a 16px root, restated here at the same 0.75 ratio
 * (rounded half-up, matching the rest of the resize) to keep token-based and literal values
 * visually consistent.
 *
 * `fontSizes` deliberately stays in `rem`: it is the one scale a reader's browser font-size
 * preference should still move. The values are chosen to land on the same whole pixels the
 * rounding gives at a default 16px root, so nothing shifts for a reader who has not changed it.
 * `space` and `radii` are px — they describe layout, not type, and should not grow with a
 * reader's type preference.
 *
 * Deliberately NOT restated here:
 * - `radii.full` — stays at Chakra's 9999px pill value.
 * - `sizes.container.*` and Chakra's named `sizes` (`xs`…`8xl`) — these describe viewport-sized
 *   containers, not design values, so they hold still for the same reason `breakpoints` does.
 * - `lineHeights` — the app sets line heights explicitly alongside each font size, in the
 *   component variants rather than as a scale. They are rem, so they track `fontSize`.
 */

export const fontSizes = {
    xs: '0.5625rem', // 9px at the default root, was 12px
    sm: '0.6875rem', // 11px, was 14px
    md: '0.75rem', // 12px, was 16px
    lg: '0.875rem', // 14px, was 18px
    xl: '0.9375rem', // 15px, was 20px
    '2xl': '1.125rem', // 18px, was 24px
    '3xl': '1.4375rem', // 23px, was 30px
    '4xl': '1.6875rem', // 27px, was 36px
    '5xl': '2.25rem', // 36px, was 48px
    '6xl': '2.8125rem', // 45px, was 60px
    '7xl': '3.375rem', // 54px, was 72px
    '8xl': '4.5rem', // 72px, was 96px
    '9xl': '6rem', // 96px, was 128px
}

export const space = {
    '0.5': '2px', // was 2px
    '1': '3px', // was 4px
    '1.5': '5px', // was 6px
    '2': '6px', // was 8px
    '2.5': '8px', // was 10px
    '3': '9px', // was 12px
    '3.5': '11px', // was 14px
    '4': '12px', // was 16px
    '5': '15px', // was 20px
    '6': '18px', // was 24px
    '7': '21px', // was 28px
    '8': '24px', // was 32px
    '9': '27px', // was 36px
    '10': '30px', // was 40px
    '12': '36px', // was 48px
    '14': '42px', // was 56px
    '16': '48px', // was 64px
    '20': '60px', // was 80px
    '24': '72px', // was 96px
    '28': '84px', // was 112px
    '32': '96px', // was 128px
    '36': '108px', // was 144px
    '40': '120px', // was 160px
    '44': '132px', // was 176px
    '48': '144px', // was 192px
    '52': '156px', // was 208px
    '56': '168px', // was 224px
    '60': '180px', // was 240px
    '64': '192px', // was 256px
    '72': '216px', // was 288px
    '80': '240px', // was 320px
    '96': '288px', // was 384px
}

export const radii = {
    sm: '2px', // was 2px
    base: '3px', // was 4px
    md: '5px', // was 6px
    lg: '6px', // was 8px
    xl: '9px', // was 12px
    '2xl': '12px', // was 16px
    '3xl': '18px', // was 24px
}

/**
 * Chakra composes `sizes` as `{ ...spacing, ...largeSizes, container }`, so it embeds its own copy
 * of the spacing scale. Overriding `space` alone leaves it untouched, which would make `w={4}`
 * (16px) disagree with `p={4}` (12px). Restating the numeric keys keeps them in step; `extendTheme`
 * merges, so the named sizes and `container.*` above are preserved.
 */
export const sizes = { ...space }

/**
 * Minimum hit area for interactive controls on touch devices (WCAG 2.5.5 Target Size).
 *
 * Released at `md`, where the pointer is a mouse and a tighter control is fine. This is a fixed
 * accessibility floor, not a design value — it does NOT move with the 0.75 resize above.
 *
 * How it is applied is the consumer's decision: `Button.ts` expands a hit area around the control
 * rather than growing the control itself, and explains there why. Controls with no box to grow use
 * responsive padding instead — see `Tabs.ts`.
 */
export const MOBILE_TOUCH_TARGET = { base: '44px', md: '0' }
